import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  BellIcon,
  PaperAirplaneIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api } from '../services/api';
import NotificationPreview from './composer/NotificationPreview';

interface NewsItem {
  news_id: number;
  date?: string;
  month?: string;
  news_date: string;
  subject: string;
  sub_category?: string;
  image_url?: string;
  title: string;
  news_text: string;
  quiz_topic?: string;
  extra?: string;
  notification_sent?: number;
  notification_sent_at?: string;
  notification_id?: number;
}

interface NewsNotificationModalProps {
  news: NewsItem;
  onClose: () => void;
  onSuccess?: (result: any) => void;
}

export default function NewsNotificationModal({
  news,
  onClose,
  onSuccess,
}: NewsNotificationModalProps) {
  const queryClient = useQueryClient();
  const [targetType, setTargetType] = useState<'all' | 'active_7d'>('all');
  const [priority, setPriority] = useState<'low' | 'normal' | 'high' | 'critical'>('normal');
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Strip HTML tags for preview
  const stripHtml = (html: string): string => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };

  // Calculate preview with character counts
  const calculatePreview = () => {
    const titlePlain = stripHtml(news.title);
    const bodyPlain = stripHtml(news.news_text);

    const titleTruncated =
      titlePlain.length > 100 ? titlePlain.substring(0, 97) + '...' : titlePlain;

    const bodyTruncated =
      bodyPlain.length > 200 ? bodyPlain.substring(0, 197) + '...' : bodyPlain;

    return {
      title: titleTruncated,
      body: bodyTruncated,
      titleChars: titlePlain.length,
      bodyChars: bodyPlain.length,
    };
  };

  const preview = calculatePreview();

  // Get user count description
  const getUserCount = (type: 'all' | 'active_7d'): string => {
    return type === 'all' ? '24 users' : '~12 active users';
  };

  // Send notification mutation
  const sendMutation = useMutation({
    mutationFn: () =>
      api.post(`/admin/news/${news.news_id}/send-notification`, {
        target_type: targetType,
        target_segment: targetType === 'active_7d' ? 'active_7d' : null,
        priority: priority,
        send_immediately: true,
      }),
    onSuccess: (response) => {
      const stats = response.data.stats;
      toast.success(
        `✓ Notification sent to ${stats.total_recipients} users! ` +
          `(${stats.fcm_success} delivered${stats.fcm_failed > 0 ? `, ${stats.fcm_failed} failed` : ''})`,
        { duration: 5000 }
      );
      queryClient.invalidateQueries({ queryKey: ['news'] });
      if (onSuccess) onSuccess(response.data);
      onClose();
    },
    onError: (error: any) => {
      console.error('Send notification error:', error.response?.data);
      const detail = error.response?.data?.detail;

      let message = 'Failed to send notification';
      if (error.response?.status === 404) {
        message = 'News article not found';
      } else if (error.response?.status === 403) {
        message = 'Permission denied: Admin access required';
      } else if (error.response?.status === 400) {
        message = typeof detail === 'string' ? detail : 'Invalid request';
      } else if (typeof detail === 'string') {
        message = detail;
      }

      toast.error(message, { duration: 5000 });
    },
  });

  // Handle send button click (two-step confirmation)
  const handleSendClick = () => {
    if (!showConfirmation) {
      setShowConfirmation(true);
      return;
    }
    // Second click - actually send
    sendMutation.mutate();
  };

  // Prevent backdrop close while sending
  const handleBackdropClick = () => {
    if (sendMutation.isPending) {
      toast.error('Please wait for notification to send', { duration: 2000 });
      return;
    }
    onClose();
  };

  // Validation warnings
  const warnings: string[] = [];
  if (preview.titleChars < 10) {
    warnings.push('Title is very short for a notification');
  }
  if (preview.bodyChars < 20) {
    warnings.push('Content is very short for a notification');
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
              <BellIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Send as Notification
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Push this article to user devices
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={sendMutation.isPending}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-50"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Body: Two-column layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 overflow-y-auto max-h-[calc(90vh-220px)]">
          {/* LEFT COLUMN: Controls */}
          <div className="space-y-5">
            {/* Article Info Card */}
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
              <h3 className="font-semibold text-sm text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
                Article Information
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Subject:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {news.subject}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Date:</span>
                  <span className="text-gray-900 dark:text-white">
                    {new Date(news.news_date).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between pt-2 border-t border-gray-200 dark:border-gray-600">
                  <span className="text-gray-500 dark:text-gray-400">Original Title:</span>
                  <span className="text-xs font-mono text-gray-600 dark:text-gray-300">
                    {preview.titleChars} chars
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Original Content:</span>
                  <span className="text-xs font-mono text-gray-600 dark:text-gray-300">
                    {preview.bodyChars} chars
                  </span>
                </div>
              </div>
            </div>

            {/* Target Audience */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-3">
                Target Audience
              </label>
              <div className="space-y-2">
                <label className="flex items-center p-3 border-2 rounded-lg cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50 border-gray-300 dark:border-gray-600 has-[:checked]:border-blue-600 has-[:checked]:bg-blue-50 dark:has-[:checked]:bg-blue-900/20">
                  <input
                    type="radio"
                    name="target"
                    checked={targetType === 'all'}
                    onChange={() => setTargetType('all')}
                    className="mr-3 w-4 h-4 text-blue-600"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 dark:text-white">
                      All Users
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Send to everyone ({getUserCount('all')})
                    </div>
                  </div>
                </label>
                <label className="flex items-center p-3 border-2 rounded-lg cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50 border-gray-300 dark:border-gray-600 has-[:checked]:border-blue-600 has-[:checked]:bg-blue-50 dark:has-[:checked]:bg-blue-900/20">
                  <input
                    type="radio"
                    name="target"
                    checked={targetType === 'active_7d'}
                    onChange={() => setTargetType('active_7d')}
                    className="mr-3 w-4 h-4 text-blue-600"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 dark:text-white">
                      Active Users (Last 7 Days)
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Users who answered questions recently ({getUserCount('active_7d')})
                    </div>
                  </div>
                </label>
              </div>
            </div>

            {/* Priority */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-3">
                Priority
              </label>
              <div className="grid grid-cols-3 gap-2">
                {(['low', 'normal', 'high'] as const).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPriority(p)}
                    className={`px-4 py-2 rounded-lg border-2 transition-all font-medium ${
                      priority === p
                        ? 'border-blue-600 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                        : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Preview Info Box */}
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 space-y-2 text-sm border border-blue-200 dark:border-blue-700">
              <div className="font-semibold text-blue-900 dark:text-blue-200 mb-2">
                Notification Character Counts
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Title:</span>
                <span
                  className={`font-mono text-xs font-semibold ${
                    preview.titleChars > 100
                      ? 'text-orange-600 dark:text-orange-400'
                      : 'text-gray-600 dark:text-gray-300'
                  }`}
                >
                  {preview.titleChars}/100 chars
                  {preview.titleChars > 100 && ' (will truncate)'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Body:</span>
                <span
                  className={`font-mono text-xs font-semibold ${
                    preview.bodyChars > 200
                      ? 'text-orange-600 dark:text-orange-400'
                      : 'text-gray-600 dark:text-gray-300'
                  }`}
                >
                  {preview.bodyChars}/200 chars
                  {preview.bodyChars > 200 && ' (will truncate)'}
                </span>
              </div>
            </div>

            {/* Warnings if any */}
            {warnings.length > 0 && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-3">
                <div className="flex gap-2">
                  <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-500 flex-shrink-0" />
                  <div className="text-sm">
                    {warnings.map((warning, idx) => (
                      <p key={idx} className="text-yellow-800 dark:text-yellow-200">
                        {warning}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* RIGHT COLUMN: Preview */}
          <div>
            <h3 className="font-semibold text-sm text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
              Preview on User Devices
            </h3>
            <NotificationPreview
              title={preview.title}
              body={preview.body}
              imageUrl={news.image_url}
              type="news"
              category={news.subject}
              actions={[
                { label: 'Read Article', style: 'primary' },
                { label: 'Share', style: 'secondary' },
              ]}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-3 italic">
              This is how users will see the notification
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 bg-gray-50 dark:bg-gray-700/30">
          {/* Warning or Confirmation Message */}
          {!showConfirmation ? (
            <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg flex items-start gap-3">
              <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <strong className="text-yellow-900 dark:text-yellow-200">
                  Sending to {getUserCount(targetType)}
                </strong>
                <p className="text-yellow-800 dark:text-yellow-300 mt-1">
                  This action cannot be undone. Users will receive a push notification
                  immediately.
                </p>
              </div>
            </div>
          ) : (
            <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg flex items-start gap-3">
              <CheckCircleIcon className="w-5 h-5 text-green-600 dark:text-green-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <strong className="text-green-900 dark:text-green-200">
                  Ready to send!
                </strong>
                <p className="text-green-800 dark:text-green-300 mt-1">
                  Click "Confirm Send" again to send the notification
                </p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={sendMutation.isPending}
              className="px-6 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSendClick}
              disabled={sendMutation.isPending || warnings.length > 0}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                showConfirmation
                  ? 'bg-green-600 hover:bg-green-700 text-white shadow-lg shadow-green-600/30'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {sendMutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  Sending...
                </>
              ) : showConfirmation ? (
                <>
                  <PaperAirplaneIcon className="w-4 h-4" />
                  Confirm Send
                </>
              ) : (
                <>
                  <BellIcon className="w-4 h-4" />
                  Send Notification
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
